import { createRouter, createWebHashHistory, type RouteRecordRaw } from 'vue-router'
import { sessionStorage } from '@/utils/storage'
import { generateRoutesFromMenu } from '@/utils/generateRoutes'
import { getMenuTree } from '@/api/userGroup'
import { useMenuStore } from '@/stores/menu'
import { usePermissionStore } from '@/stores/permission'
import { createPinia } from 'pinia'

const Layout = () => import('@/layout/Layout.vue')
// 白名单路由
const whiteList = ['/login', '/scan-login-success']
// 判断是否是刷新页面进入的守卫
let isRefresh = false
export const constantRoutes = [
  {
    path: '/',
    meta: { hidden: true }
  },
  {
    path: '/login',
    name: 'login',
    meta: { hidden: true },
    component: () => import('../pages/Login/Pages/index.vue'),
  },
  {
    path: '/scan-login-success',
    name: 'scanLoginSuccess',
    meta: { hidden: true, public: true },
    component: () => import('../pages/Login/Pages/ScanLoginSuccess.vue'),
  },
  {
    path: '/ZZZTEST',
    name: 'ZZZTEST',
    meta: { hidden: true },
    component: () => import('../pages/ZZZTEST/index.vue'),
  },
]

const router = createRouter({
  history: createWebHashHistory(),
  routes: constantRoutes,
  // 刷新时，滚动条位置还原
  scrollBehavior: () => ({ left: 0, top: 0 }),
})

// 是否已添加动态路由
let hasAddedRoutes = false
let defaultDynamicRoutePath = ''

/**
 * 兼容 App 扫码打开的服务器路径链接，例如 /admin?qrToken=xxx。
 * 项目使用 hash history，这类参数位于 hash 之前，Vue Router 不会放入
 * to.query，因此需要在首次导航时手动转成登录页查询参数。
 */
function getExternalQrToken(): string {
  if (typeof window === 'undefined') return ''

  const value = new URLSearchParams(window.location.search).get('qrToken')
  return value?.trim() || ''
}

/** 从已注册的后端菜单中找到首个可访问的叶子页面。 */
function getFirstAccessibleRoutePath(routes: RouteRecordRaw[]): string {
  for (const route of routes) {
    if (route.meta?.hidden) continue

    const childPath = route.children?.length
      ? getFirstAccessibleRoutePath(route.children)
      : ''

    if (childPath) return childPath
    if (route.component && !route.children?.length && route.name) {
      const resolvedRoute = router.resolve({ name: route.name })
      if (resolvedRoute.matched.length) return resolvedRoute.path
    }
  }
  return ''
}

// 路由守卫
router.beforeEach(async (to, from, next) => {
  const info = sessionStorage.get('userInfo')
  const needResetPwd = info?.needResetPwd

  const token = sessionStorage.get('token'+info?.id)
  const dialogShow = sessionStorage.get('dialogShow')
  console.log('路由守卫', token)

  // 扫码链接可能是 /admin?qrToken=xxx，参数不在 hash 路由的 to.query 中。
  // 必须优先于 token 判断，否则已登录浏览器会直接进入后台。
  const externalQrToken = getExternalQrToken()
  if (externalQrToken && to.path !== '/login' && to.path !== '/scan-login-success') {
    // 保留 token 到 hash 路由，同时移除服务器查询串，避免后续普通导航重复触发该分支。
    if (typeof window !== 'undefined') {
      window.history.replaceState(window.history.state, '', `${window.location.pathname}${window.location.hash}`)
    }
    next({ path: '/login', query: { qrToken: externalQrToken } })
    return
  }

  // 带 qrToken 的登录页是扫码确认流程，即使浏览器已有后台 token 也不能跳过。
  const isQrLoginRoute = typeof to.query.qrToken === 'string' && Boolean(to.query.qrToken.trim())
  if (to.path === '/login' && isQrLoginRoute) {
    next()
    return
  }

  // 扫码登录成功页用于承接外部回调，始终允许直接访问。
  if (to.path === '/scan-login-success') {
    next()
    return
  }

  // 修改判断逻辑
  if (token) {
    // 如果需要重置密码或对话框正在显示，强制停留在登录页
    if (needResetPwd === 1 || dialogShow === 'true') {
      if (to.path !== '/login') {
        next('/login')
      } else {
        next()
      }
      return
    }

    // 正常登录后的路由处理
    if (to.path === '/login') {
      next({ path: '/' })
    } else {
      if (!hasAddedRoutes) {
        try {
          isRefresh = true
          const menuData = await getMenuTree()
          const menuStore = useMenuStore()
          const permissionStore = usePermissionStore()
          
          menuStore.setMenuList(menuData)
          permissionStore.setMenuList(menuData)
          
          // 设置当前页面的按钮权限
          permissionStore.setCurrentPagePermissions(to.path)
          
          const routes = generateRoutesFromMenu(menuData)
          
          // 添加动态路由
          for (const route of routes) {
            router.addRoute(route)
          }
          defaultDynamicRoutePath = getFirstAccessibleRoutePath(routes)
          if (!defaultDynamicRoutePath) {
            throw new Error('当前账号未配置可访问页面')
          }
          console.log('routes', routes)
          hasAddedRoutes = true
          
          const isCurrentRouteRegistered = router.resolve(to.fullPath).matched.length > 0
          if (to.path === '/' || !isCurrentRouteRegistered) {
            next({ path: defaultDynamicRoutePath, replace: true })
          } else {
            next({ ...to, replace: true })
          }
        } catch (error) {
          console.error('加载动态路由失败:', error)
          next(false)
        }
      } else {
        // 路由变化时更新当前页面的按钮权限
        const permissionStore = usePermissionStore()
        permissionStore.setCurrentPagePermissions(to.path)
        if (to.path === '/') {
          if (!defaultDynamicRoutePath) {
            next(false)
            return
          }
          next({ path: defaultDynamicRoutePath, replace: true })
        } else {
          next()
        }
      }
    }
  } else {
    if (whiteList.includes(to.path)) {
      next()
    } else {
      next('/login')
    }
  }
})

// 添加重置路由的方法
export const resetRouter = () => {
  // 移除所有动态添加的路由
  const removeRoutes = router.getRoutes()
  removeRoutes.forEach(route => {
    if (route.name) {
      router.removeRoute(route.name)
    }
  })
  
  // 重新添加固定路由
  constantRoutes.forEach(route => {
    router.addRoute(route)
  })
  
  // 重置路由添加状态
  hasAddedRoutes = false
  defaultDynamicRoutePath = ''
}

/**
 * 重新拉取当前用户菜单并热更新动态路由（菜单增删改后调用，无需整页刷新）
 */
export async function refreshDynamicRoutes(currentPath?: string) {
  const menuData = await getMenuTree()
  const menuStore = useMenuStore()
  const permissionStore = usePermissionStore()
  const fullPath = router.currentRoute.value.fullPath

  resetRouter()

  const routes = generateRoutesFromMenu(menuData)
  for (const route of routes) {
    router.addRoute(route)
  }
  defaultDynamicRoutePath = getFirstAccessibleRoutePath(routes)
  hasAddedRoutes = true

  // 最后更新 store，便于 Layout 等监听方基于最新路由重建菜单
  menuStore.setMenuList(menuData)
  permissionStore.setMenuList(menuData)
  if (currentPath) {
    permissionStore.setCurrentPagePermissions(currentPath)
  }

  // 重新匹配当前地址，避免重置路由后页面状态异常
  await router.replace(fullPath)

  return routes
}

export default router
